import { popinApi } from '../api/api';
import { ActivityTypes } from './activitytypes';
import mixpanel from 'mixpanel-browser';
import { ACTIVE_USER_TRACKING_DEBOUNCE_MINUTES, APP_BID, APP_ID, APP_TYPE } from '../constants';
import store from '../store/configureStore';
import _ from 'lodash';
import { EmailError } from '../models/EmailError';
import DateTime from '../models/DateTime';
import Logger from '../util/Logger';
import $ from 'jquery';
import User from '../models/User';
import Event from '../models/Event';
import Collection from '../models/Collection';
import Tenant from '../models/Tenant';

/**
 * Track generic activity and send to our own servers, at '/activity' endpoint
 *
 * @param type {string}
 * @param extras [{{detail: [string], event_id: [string]}}]
 */
const trackActivity = (type, extras = undefined) => {
  const payload = { type };
  if (extras) {
    if (extras.detail) {
      payload.detail = extras.detail;
    }

    if (extras.event_id) {
      payload.event_id = extras.event_id;
    }

    if (extras.page_name) {
      payload.page_name = extras.page_name;
    }
  }
  // Fire and forget
  Logger.info(`TrackingAnalytics: Activity: ${type}`, payload);
  popinApi.post('/activity', payload);
};

// A list of all the currently tracked active user pages
const activeUserTracking = [];

export const TrackingAnalytics = {

  NA: 'NA',

  /**
   * Initialize TrackingAnalytics
   */
  init () {
    const appEnv = window.location.hostname.slice(0, 3);
    let mixpanelToken = 'be55bb340849b6f2cffb009d1f26a647';
    let debug = true;
    let local = false;
    if (appEnv === 'loc') local = true;
    if (appEnv === 'dev') mixpanelToken = '10abd57c56d0a1cac56fe09370aecf7c';
    if (appEnv === 'stg') mixpanelToken = 'ef12d80afbd499a988e1b50fec231a62';
    if (appEnv === 'pre') mixpanelToken = '8a2dc828d7f0a3cd50b2279b456f48ff';
    if (appEnv === 'app') {
      mixpanelToken = '0f2f161799cca6216424c9f8d72736a9';
      debug = false;
    }
    mixpanel.init(mixpanelToken);
    mixpanel.set_config({
      cross_subdomain_cookie: false,
      debug: debug,
      secure_cookie: !local
    });
    mixpanel.reset();
  },

  /**
   * Track Page View events (no Angulartics for us)
   */
  mixpanelTrackPageViewed () {
    mixpanel.track('Page Viewed', { 'Current URL': window.location.href });
  },

  trackPageViewed (eventId, pageName) {
    trackActivity(ActivityTypes.PAGE_VIEW, {
      event_id: eventId,
      page_name: pageName
    });
  },

  /**
   * Property object must have at least three functions
   *
   * one named hasReachedBottom()
   * one named setScrollPercentage(double)
   * one named setReachedBottom(boolean)
   *
   * This is what we call max spaghet.
   */
  trackPageScrolledToBottom (elementRef, eventId, pageName, propertyObject) {
    const validatePropertyObject = (propertyObject) => {
      if (!(!!propertyObject.hasReachedBottom &&
        typeof propertyObject.hasReachedBottom === 'function')) {
        Logger.assert('propertyObject missing hasReachedBottom callback');
      }

      if (!(!!propertyObject.setScrollPercentage &&
        typeof propertyObject.setScrollPercentage === 'function')) {
        Logger.assert('propertyObject missing setScrollPercentage callback');
      }

      if (!(!!propertyObject.setReachedBottom &&
        typeof propertyObject.setReachedBottom === 'function')) {
        Logger.assert('propertyObject missing setReachedBottom callback');
      }
    };

    validatePropertyObject(propertyObject);
    const callback = () => {
      const w = $(window);
      const elem = $(`#${elementRef.id}`);
      // Can be undefined when page is not fully rendered
      if (elem.offset) {
        const offset = elem.offset();
        if (!offset) {
          return;
        }
        const elementTop = offset.top;
        const elementBottom = elementTop + elem.outerHeight();
        const bottom = w.scrollTop() + window.innerHeight;
        const top = w.scrollTop();

        const s = w.scrollTop();
        const d = $(document).height();
        const c = $(window).height();
        // d - c is zero when there is no place to scroll so result is NaN, handle in analytics
        const scrollPercent = (s / (d - c)) * 100;
        propertyObject.setScrollPercentage(scrollPercent);

        if ((bottom > elementTop) && (top < elementBottom)) {
          // The element is visible, do something
          if (!propertyObject.hasReachedBottom()) {
            trackActivity(ActivityTypes.SCROLL_TO_BOTTOM, {
              event_id: eventId,
              page_name: pageName
            });
            propertyObject.setReachedBottom(true);
          }
        }
        else {
          // The element is not visible, do something else
          propertyObject.setReachedBottom(false);
        }
      }
    };

    if (!!elementRef) {
      Logger.info('Begin listening for bottom scroll');
      $(window).on('scroll', callback);
    } else {
      Logger.info('Stop listening for bottom scroll');
      $(window).off('scroll', callback);
    }
  },

  listenForActiveUser (domElement, detail) {
    if (!!domElement) {
      // Component is mounting, track active
      const activeUser = new ActiveUserTracker(domElement, detail);
      activeUserTracking.push(activeUser);
      activeUser.startTracking();
    } else {
      // Component is unmounting, stop active tracking

      // Cache size for V8 compiler optimization
      const size = activeUserTracking.length;
      for (let i = 0; i < size; ++i) {
        const activeUser = activeUserTracking[i];
        if (activeUser.name === detail) {
          activeUser.stopTracking();
          activeUserTracking.splice(i, 1);
          break;
        }
      }
    }
  },

  getSuperProps: function () {
    // Mixpanel common properties spec: https://docs.google.com/spreadsheets/d/1VEUfBwpsD00TWGIOTi-0DGOWH7vDNwyUbLO2MA1ipoA/edit#gid=1871747546
    const superProps = {};

    // Constants
    superProps.app_id = APP_ID;
    superProps.app_bid = APP_BID;
    superProps.app_type = APP_TYPE;

    return superProps;
  },

  getUserProps: function (superProps, user) {
    if(!superProps) {
      Logger.error('Cannot call getUserProps without superProps');
    }
    if(!user) {
      Logger.error('Cannot call getUserProps without user object');
    }
    // User type is either 'test', 'guest', or 'normal
    let userType = 'normal';

    // Guests may not have tenants, check instead of assuming
    if (User.hasTenant(user)) {
      if (Tenant.isTest(User.getTenant(user))) {
        userType = 'test';
      }
    } else if (User.isGuest(user)) {
      userType = 'guest';
    }

    superProps.user_type = userType;
    superProps.user_has_manage_launched = User.hasManageLaunched(user);
    superProps.user_has_self_launched = User.hasSelfLaunched(user);
    superProps.user_id = User.getId(user);
    superProps.user_email = User.getEmail(user);

    // Only add tenant id if the tenant exists, don't send undefined
    if (User.hasTenant(user)) {
      const tenant = User.getTenant(user);
      superProps.tenant_id = Tenant.getId(tenant);
      superProps.tenant_name = Tenant.getName(tenant);
    }

    // Only send the delegate_user_id if user is delegating, don't send undefined
    const delegate = User.getDelegate(user);
    if (!!delegate) {
      superProps.delegate_user_id = User.getId(delegate);
    }
    return superProps;
  },

  getEventProps: function(superProps, event) {
    if(!superProps) {
      Logger.error('Cannot call getEventProps without superProps');
    }
    if(!event) {
      Logger.error('Cannot call getEventProps without event object');
    }
    superProps.question_anonymity = Event.isAnonymous(event) || 'NA';
    superProps.visibility_setting = Event.getVisibilityType(event) || 'NA';
    superProps.question_id = Event.getId(event) || 'NA';
    superProps.question_has_collection = !!Event.getCollection(event) || 'NA';
    superProps.question_has_added_people = Event.getVisibilityType(event) === 'invite_only' ? Event.getInvitedUsersCount(event) > 0 : 'NA';
    superProps.question_style = Event.getQuestionType(event) || 'NA';
    superProps.question_phase = Event.getPhase(event) || 'NA';
    superProps.question_hero = Event.isUserCreated(event) || 'NA';

    return superProps;
  },
  getCollectionProps: function(superProps, collection) {
    if(!superProps) {
      Logger.error('Cannot call getCollectionProps without superProps');
    }
    if(!collection) {
      Logger.error('Cannot call getCollectionProps without collection object');
    }
    superProps.collection_phase = Collection.getPhase(collection);
    superProps.collection_type = Collection.getType(collection);
    superProps.collection_has_name = !!Collection.getName(collection) || false;
    superProps.collection_concurrent = !!Collection.isConcurrentEnabled(collection);
    superProps.collection_id = Collection.getId(collection);

    return superProps;
  },

  setTimer: function (eventName) {
    if (!eventName) {
      throw new Error('name is required to call mixpanel api');
    }
    mixpanel.time_event(eventName);
  },

  send (name, props = null, callback = null) {
    if (!name) {
      Logger.error('mixpanel event is missing a name');
      callback();
    }
    const user = store.getState().user.user;
    const event = store.getState().event.event;
    let collection = null;
    if (!!event && event.collection) {
      collection = event.collection;
    }
    let superProps = this.getSuperProps();

    if (user) {
      this.getUserProps(superProps, user);
      if (event) {
        this.getEventProps(superProps, event);
      }
      if (collection) {
        this.getCollectionProps(superProps, collection);
      }
      // merge default props with any activity-specific props
      const payload = Object.assign({}, superProps, props);
      Logger.info('TrackingAnalytics:', name, payload);
      mixpanel.track(name, payload, callback);
    } else {
      Logger.error('Unable to send to Mixpanel, no User object from Redux');
    }
  },
  handleInvalidEmails (prevErrorProp, nextErrorProp, callback) {
    if (nextErrorProp && nextErrorProp.length > 0 && nextErrorProp !== prevErrorProp
      && prevErrorProp && prevErrorProp.length === 0) {
      if (nextErrorProp.length !== 0) {
        const listOfListOfEmails = _.chain(nextErrorProp).map(e => EmailError.getEmails(e)).value();
        const listOfEmails = _.flattenDeep(listOfListOfEmails);
        callback(listOfEmails);
      }
    }
  },

  gtag (event, attributes) {
      if (window.dataLayer) {
        const payload = {
          event: event,
          attributes: {
            route: window.location.toString(),
            pageName: document.title
          }
        };

        if (!!attributes) {
          for (let prop in attributes) {
            payload.attributes[prop] = attributes[prop];
          }
        }

        Logger.info('GTAG', payload);
        window.dataLayer.push(payload);
      }
  }
};

class ActiveUserTracker {

  constructor (domElement, name) {
    this.domElement = domElement;
    this.name = name;
    this.lastTrackedTimeSeconds = 0;

    // Bind the internal function to the scope of the object instead of the scope of the window callback
    // or else this.`anything` is undefined.
    this.trackActiveUser = this.trackActiveUser.bind(this);
  }

  startTracking () {
    if (!!this.domElement) {
      Logger.info('Begin tracking for ', this.name);
      this.domElement.addEventListener('mouseenter', this.trackActiveUser);
    }
  }

  stopTracking () {
    if (!!this.domElement) {
      Logger.info('Finish tracking for ', this.name);
      this.domElement.removeEventListener('mouseenter', this.trackActiveUser);
    }
  }

  trackActiveUser () {
    const lastMoment = DateTime.unixSecondsToMoment(this.lastTrackedTimeSeconds);
    const lastMomentWithDebounce = lastMoment.add(ACTIVE_USER_TRACKING_DEBOUNCE_MINUTES, 'minutes');
    const now = DateTime.now();
    if (lastMomentWithDebounce.isBefore(now)) {
      Logger.info('Tracking Active User: ', this.name);
      trackActivity(ActivityTypes.ACTIVE_USER, { detail: this.name });
      this.lastTrackedTimeSeconds = DateTime.momentToUnixSeconds(now);
    } else {
      Logger.info('Not tracking active user until timeout elapses');
    }
  }
}
